/**
 * VirtualScroller - Efficient Large Sequence Rendering
 *
 * Implements virtual scrolling for genome sequences that can be 100k+ bases.
 * Only renders visible rows plus overscan for smooth scrolling at 60fps.
 */

export interface VirtualScrollerOptions {
  /** Total number of items (characters in sequence) */
  totalItems: number;
  /** Width of each item in pixels */
  itemWidth: number;
  /** Height of each item in pixels */
  itemHeight: number;
  /** Viewport width in pixels */
  viewportWidth: number;
  /** Viewport height in pixels */
  viewportHeight: number;
  /** Number of rows to render outside viewport for smooth scrolling */
  overscan?: number;
  /** Enable smooth momentum scrolling */
  momentum?: boolean;
  /** Momentum friction coefficient (0-1, lower = more friction) */
  friction?: number;
  /** Minimum velocity before stopping momentum */
  minVelocity?: number;
  /** Rubber-band factor for overscroll (0-1, higher = stretchier) */
  rubberBand?: number;
  /** Maximum overscroll distance in pixels */
  maxOverscroll?: number;
  /** Snap scrolling to multiples of N items (e.g., 3 for codon alignment) */
  snapToMultiple?: number | null;
  /** Duration in ms for snap animation (simple linear) */
  snapAnimationMs?: number;
  /** Damping applied when bouncing off edges */
  bounceDamping?: number;
}

export interface VisibleRange {
  /** Start index in the sequence */
  startIndex: number;
  /** End index in the sequence (exclusive) */
  endIndex: number;
  /** Start row in the grid */
  startRow: number;
  /** End row in the grid (exclusive) */
  endRow: number;
  /** Y offset for first visible row (for smooth sub-row scrolling) */
  offsetY: number;
  /** X offset for first visible column (for smooth sub-column scrolling) */
  offsetX: number;
  /** Total visible items */
  visibleCount: number;
}

export interface ScrollState {
  /** Scroll position in pixels (X) */
  scrollX: number;
  /** Scroll position in pixels (Y) */
  scrollY: number;
  /** Velocity X (for momentum) */
  velocityX: number;
  /** Velocity Y (for momentum) */
  velocityY: number;
  /** Is currently animating momentum */
  isAnimating: boolean;
}

const DEFAULT_OPTIONS: Required<VirtualScrollerOptions> = {
  totalItems: 0,
  itemWidth: 16,
  itemHeight: 20,
  viewportWidth: 800,
  viewportHeight: 600,
  overscan: 3,
  momentum: true,
  friction: 0.95, // Higher = longer coast; closer to native feel
  minVelocity: 0.5,
  rubberBand: 0.5,
  maxOverscroll: 50,
  snapToMultiple: null,
  snapAnimationMs: 150,
  bounceDamping: 0.3,
};

export class VirtualScroller {
  private options: Required<VirtualScrollerOptions>;
  private state: ScrollState;
  private animationFrameId: number | ReturnType<typeof setTimeout> | null = null;
  private onScrollCallback: ((range: VisibleRange) => void) | null = null;
  private useNativeRaf: boolean;

  // Layout calculations
  private cols: number = 0;
  private rows: number = 0;
  private totalHeight: number = 0;
  private totalWidth: number = 0;

  constructor(options: VirtualScrollerOptions) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.state = {
      scrollX: 0,
      scrollY: 0,
      velocityX: 0,
      velocityY: 0,
      isAnimating: false,
    };
    this.recalculateLayout();
    this.useNativeRaf = typeof globalThis.requestAnimationFrame === 'function';
  }

  /**
   * Recalculate layout when options change
   */
  private recalculateLayout(): void {
    const { totalItems, itemWidth, itemHeight, viewportWidth } = this.options;

    // Calculate how many columns fit in the viewport
    this.cols = Math.max(1, Math.floor(viewportWidth / itemWidth));

    // Calculate total rows needed
    this.rows = Math.ceil(totalItems / this.cols);

    // Calculate total scrollable dimensions
    this.totalHeight = this.rows * itemHeight;
    this.totalWidth = this.cols * itemWidth;
  }

  /**
   * Update options and recalculate layout
   */
  updateOptions(options: Partial<VirtualScrollerOptions>): void {
    this.options = { ...this.options, ...options };
    this.recalculateLayout();

    // Clamp scroll position to valid range
    this.state.scrollY = Math.max(
      0,
      Math.min(this.state.scrollY, this.maxScrollY)
    );
    this.state.scrollX = Math.max(
      0,
      Math.min(this.state.scrollX, this.maxScrollX)
    );
  }

  /**
   * Compute a range for the current scroll position.
   *
   * `overscanRows` expands the returned row range beyond the viewport for rendering.
   * UI and input logic should typically use `overscanRows = 0` (see `getVisibleRange()`).
   */
  private computeRange(overscanRows: number): VisibleRange {
    const { itemHeight, itemWidth, viewportWidth, viewportHeight } = this.options;
    const overscan = Math.max(0, Math.floor(overscanRows));
    const { scrollX, scrollY } = this.state;
    // The sequence grid layout currently guarantees totalWidth <= viewportWidth, so maxScrollX is 0.
    // If we allow rubber-band overscroll on X in that case, tiny trackpad diagonal deltas can shift
    // startCol/startIndex even though the renderer does not implement horizontal panning.
    // Clamp X to 0 when horizontal scrolling isn't possible to keep range/startIndex stable.
    const effectiveScrollX = this.maxScrollX === 0 ? 0 : scrollX;

    // Calculate visible row range
    const startRow = Math.max(0, Math.floor(scrollY / itemHeight) - overscan);
    const visibleRows = Math.ceil(viewportHeight / itemHeight) + overscan * 2;
    const endRow = Math.min(this.rows, startRow + visibleRows);

    // Calculate visible column range (for horizontal scrolling if needed)
    const startCol = Math.max(0, Math.floor(effectiveScrollX / itemWidth));
    const visibleCols = Math.ceil(viewportWidth / itemWidth);
    const endCol = Math.min(this.cols, startCol + visibleCols);

    // Calculate indices
    const startIndex = startRow * this.cols + startCol;
    const endIndex = Math.min(
      this.options.totalItems,
      (endRow - 1) * this.cols + endCol
    );

    // Calculate pixel offsets for sub-cell scrolling
    // IMPORTANT: `offsetY/offsetX` must be relative to the returned `startRow/startCol`.
    // When overscanning above/left, the first rendered row/col can be earlier than the first
    // visible row/col, so we compute offsets from `startRow/startCol` rather than `%`.
    const offsetY = -(scrollY - startRow * itemHeight);
    const offsetX =
      effectiveScrollX === 0 ? 0 : -(effectiveScrollX - startCol * itemWidth);

    return {
      startIndex,
      endIndex,
      startRow,
      endRow,
      offsetY,
      offsetX,
      visibleCount: endIndex - startIndex,
    };
  }

  /**
   * Get the currently visible viewport range (no overscan).
   * This is the range UI should display and input/edge logic should use.
   */
  getVisibleRange(): VisibleRange {
    return this.computeRange(0);
  }

  /**
   * Get the range used for rendering (includes overscan rows).
   * If `overscanRows` is not provided, uses the configured option.
   */
  getRenderRange(overscanRows?: number): VisibleRange {
    return this.computeRange(overscanRows ?? this.options.overscan);
  }

  /**
   * Get maximum scroll Y position
   */
  get maxScrollY(): number {
    return Math.max(0, this.totalHeight - this.options.viewportHeight);
  }

  /**
   * Get maximum scroll X position
   */
  get maxScrollX(): number {
    return Math.max(0, this.totalWidth - this.options.viewportWidth);
  }

  /**
   * Scroll by delta
   */
  scrollBy(deltaX: number, deltaY: number): void {
    this.scrollTo(
      this.state.scrollX + deltaX,
      this.state.scrollY + deltaY
    );
  }

  /**
   * Scroll to absolute position
   */
  scrollTo(x: number, y: number): void {
    const newScrollX = this.maxScrollX === 0 ? 0 : this.applyRubberBand(x, this.maxScrollX);
    const newScrollY = this.applyRubberBand(y, this.maxScrollY);

    if (this.maxScrollX === 0 && this.state.velocityX !== 0) {
      this.state.velocityX = 0;
    }

    if (newScrollX !== this.state.scrollX || newScrollY !== this.state.scrollY) {
      this.state.scrollX = newScrollX;
      this.state.scrollY = newScrollY;
      this.notifyScrollChange();
    }
  }

  /**
   * Scroll to index (centers the index in viewport if possible)
   */
  scrollToIndex(index: number, center = true): void {
    const row = Math.floor(index / this.cols);
    const targetY = row * this.options.itemHeight;

    if (center) {
      const centerOffset = (this.options.viewportHeight - this.options.itemHeight) / 2;
      this.scrollTo(this.state.scrollX, targetY - centerOffset);
    } else {
      this.scrollTo(this.state.scrollX, targetY);
    }
  }

  /**
   * Scroll to start
   */
  scrollToStart(): void {
    this.scrollTo(0, 0);
  }

  /**
   * Scroll to end
   */
  scrollToEnd(): void {
    this.scrollTo(this.maxScrollX, this.maxScrollY);
  }

  // Touch tracking state
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartScrollX = 0;
  private touchStartScrollY = 0;
  private lastTouchX = 0;
  private lastTouchY = 0;
  private lastTouchTime = 0;
  private touchAxisLock: 'none' | 'x' | 'y' = 'none';

  /**
   * Handle wheel event
   *
   * IMPORTANT: Desktop wheel events should use DIRECT scrolling, not momentum.
   * Reasons:
   * 1. Desktop trackpads/mice already have native momentum (especially macOS)
   * 2. Adding VirtualScroller momentum on top creates "double momentum" and fighting
   * 3. Wheel events come continuously during scroll - overwriting velocity each time
   *    while momentum animation is decaying causes jerky, inconsistent scrolling
   *
   * Momentum is designed for touch FLING gestures where:
   * - User lifts finger with velocity
   * - No more input events arrive
   * - Momentum animates the deceleration
   *
   * For wheel events, the input device handles the momentum.
   */
  handleWheel(event: WheelEvent): void {
    // Calculate scroll delta
    let deltaX = event.deltaX;
    let deltaY = event.deltaY;

    // Normalize for different wheel modes
    if (event.deltaMode === 1) {
      // Line mode
      deltaX *= this.options.itemWidth;
      deltaY *= this.options.itemHeight;
    } else if (event.deltaMode === 2) {
      // Page mode
      deltaX *= this.options.viewportWidth;
      deltaY *= this.options.viewportHeight;
    }

    // Filter out small horizontal deltas during primarily vertical scrolls.
    // Trackpads often send tiny horizontal noise during vertical scrolling, which
    // breaks scroll blit optimization in the renderer (causing full redraws and jank).
    // Threshold: ignore horizontal if |deltaX| < 3px AND |deltaY| > |deltaX| * 2
    if (Math.abs(deltaX) < 3 && Math.abs(deltaY) > Math.abs(deltaX) * 2) {
      deltaX = 0;
    }
    // Similarly filter vertical noise during horizontal scrolls
    if (Math.abs(deltaY) < 3 && Math.abs(deltaX) > Math.abs(deltaY) * 2) {
      deltaY = 0;
    }

    // Horizontal panning isn't currently supported by the sequence grid renderer (and maxScrollX is 0),
    // so ignore X deltas to avoid "phantom" index drift on diagonal trackpad scroll.
    if (this.maxScrollX === 0) {
      deltaX = 0;
    }

    const currentX = this.state.scrollX;
    const currentY = this.state.scrollY;

    // Always use direct scrolling for wheel events
    // Clamp to valid range to avoid getting stuck in rubber-band overscroll
    // (Wheel events don't have a "release" event to trigger snap-back animation)
    const targetX = Math.max(0, Math.min(currentX + deltaX, this.maxScrollX));
    const targetY = Math.max(0, Math.min(currentY + deltaY, this.maxScrollY));

    if (targetX === currentX && targetY === currentY) {
      return;
    }

    event.preventDefault();

    // Stop any ongoing momentum animation - wheel takes over
    this.stopMomentum();

    this.scrollTo(targetX, targetY);
  }

  /**
   * Handle wheel deltas directly (worker-safe alternative to WheelEvent)
   * Uses direct scrolling - see handleWheel() for rationale
   */
  handleWheelDelta(deltaX: number, deltaY: number, deltaMode: 0 | 1 | 2 = 0): void {
    // Stop any ongoing momentum animation - wheel takes over
    this.stopMomentum();

    let dx = deltaX;
    let dy = deltaY;

    if (deltaMode === 1) {
      dx *= this.options.itemWidth;
      dy *= this.options.itemHeight;
    } else if (deltaMode === 2) {
      dx *= this.options.viewportWidth;
      dy *= this.options.viewportHeight;
    }

    // Filter trackpad noise (see handleWheel for full explanation)
    if (Math.abs(dx) < 3 && Math.abs(dy) > Math.abs(dx) * 2) {
      dx = 0;
    }
    if (Math.abs(dy) < 3 && Math.abs(dx) > Math.abs(dy) * 2) {
      dy = 0;
    }

    if (this.maxScrollX === 0) {
      dx = 0;
    }

    // Always use direct scrolling for wheel events
    // Clamp to valid range to avoid getting stuck in rubber-band overscroll
    const targetX = Math.max(0, Math.min(this.state.scrollX + dx, this.maxScrollX));
    const targetY = Math.max(0, Math.min(this.state.scrollY + dy, this.maxScrollY));
    this.scrollTo(targetX, targetY);
  }

  /**
   * Handle touch start
   */
  handleTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartScrollX = this.state.scrollX;
    this.touchStartScrollY = this.state.scrollY;
    this.lastTouchX = touch.clientX;
    this.lastTouchY = touch.clientY;
    this.lastTouchTime = performance.now();
    this.touchAxisLock = 'none';

    // Stop any ongoing momentum
    this.stopMomentum();
  }

  /**
   * Handle touch start with raw coordinates (worker-safe)
   */
  handleTouchStartPoint(x: number, y: number): void {
    this.touchStartX = x;
    this.touchStartY = y;
    this.touchStartScrollX = this.state.scrollX;
    this.touchStartScrollY = this.state.scrollY;
    this.lastTouchX = x;
    this.lastTouchY = y;
    this.lastTouchTime = performance.now();
    this.touchAxisLock = 'none';
    this.stopMomentum();
  }

  /**
   * Handle touch move
   */
  handleTouchMove(event: TouchEvent): void {
    if (event.touches.length !== 1) return;
    event.preventDefault(); // Prevent native scroll

    const touch = event.touches[0];
    const now = performance.now();
    const dt = Math.max(1, now - this.lastTouchTime);

    const totalDeltaX = this.touchStartX - touch.clientX;
    const totalDeltaY = this.touchStartY - touch.clientY;

    // Axis lock: avoid accidental horizontal drift when intending to scroll vertically.
    if (this.touchAxisLock === 'none') {
      const absDx = Math.abs(totalDeltaX);
      const absDy = Math.abs(totalDeltaY);
      const threshold = 8; // px
      if (absDx < threshold && absDy < threshold) {
        this.lastTouchX = touch.clientX;
        this.lastTouchY = touch.clientY;
        this.lastTouchTime = now;
        return;
      }

      const canScrollX = this.maxScrollX > 0;
      const canScrollY = this.maxScrollY > 0;
      if (!canScrollX && canScrollY) {
        this.touchAxisLock = 'y';
      } else if (canScrollX && !canScrollY) {
        this.touchAxisLock = 'x';
      } else {
        const dominance = 1.2;
        if (absDx >= absDy * dominance) {
          this.touchAxisLock = 'x';
        } else if (absDy >= absDx * dominance) {
          this.touchAxisLock = 'y';
        } else {
          this.touchAxisLock = absDx > absDy ? 'x' : 'y';
        }
      }

      // Rebase to avoid a jump when the lock is chosen.
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchStartScrollX = this.state.scrollX;
      this.touchStartScrollY = this.state.scrollY;
      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
      this.lastTouchTime = now;
      this.state.velocityX = 0;
      this.state.velocityY = 0;
      return;
    }

    // Calculate instantaneous velocity (scale to ~60fps frame time)
    let instantVx = (this.lastTouchX - touch.clientX) / dt * 16.67;
    let instantVy = (this.lastTouchY - touch.clientY) / dt * 16.67;
    if (this.touchAxisLock === 'x') instantVy = 0;
    if (this.touchAxisLock === 'y') instantVx = 0;

    // Smooth velocity with exponential moving average for better feel
    const smoothing = 0.4;
    const vx = this.state.velocityX * (1 - smoothing) + instantVx * smoothing;
    const vy = this.state.velocityY * (1 - smoothing) + instantVy * smoothing;

    // Apply scroll (inverted - drag down = scroll up)
    const deltaX = this.touchAxisLock === 'x' ? this.touchStartX - touch.clientX : 0;
    const deltaY = this.touchAxisLock === 'y' ? this.touchStartY - touch.clientY : 0;
    this.scrollTo(this.touchStartScrollX + deltaX, this.touchStartScrollY + deltaY);

    // Update tracking
    this.state.velocityX = vx;
    this.state.velocityY = vy;
    this.lastTouchX = touch.clientX;
    this.lastTouchY = touch.clientY;
    this.lastTouchTime = now;
  }

  /**
   * Handle touch move with raw coordinates (worker-safe)
   */
  handleTouchMovePoint(x: number, y: number): void {
    const now = performance.now();
    const dt = Math.max(1, now - this.lastTouchTime);

    const totalDeltaX = this.touchStartX - x;
    const totalDeltaY = this.touchStartY - y;

    if (this.touchAxisLock === 'none') {
      const absDx = Math.abs(totalDeltaX);
      const absDy = Math.abs(totalDeltaY);
      const threshold = 8;
      if (absDx < threshold && absDy < threshold) {
        this.lastTouchX = x;
        this.lastTouchY = y;
        this.lastTouchTime = now;
        return;
      }

      const canScrollX = this.maxScrollX > 0;
      const canScrollY = this.maxScrollY > 0;
      if (!canScrollX && canScrollY) {
        this.touchAxisLock = 'y';
      } else if (canScrollX && !canScrollY) {
        this.touchAxisLock = 'x';
      } else {
        const dominance = 1.2;
        if (absDx >= absDy * dominance) {
          this.touchAxisLock = 'x';
        } else if (absDy >= absDx * dominance) {
          this.touchAxisLock = 'y';
        } else {
          this.touchAxisLock = absDx > absDy ? 'x' : 'y';
        }
      }

      this.touchStartX = x;
      this.touchStartY = y;
      this.touchStartScrollX = this.state.scrollX;
      this.touchStartScrollY = this.state.scrollY;
      this.lastTouchX = x;
      this.lastTouchY = y;
      this.lastTouchTime = now;
      this.state.velocityX = 0;
      this.state.velocityY = 0;
      return;
    }

    let instantVx = (this.lastTouchX - x) / dt * 16.67;
    let instantVy = (this.lastTouchY - y) / dt * 16.67;
    if (this.touchAxisLock === 'x') instantVy = 0;
    if (this.touchAxisLock === 'y') instantVx = 0;

    const smoothing = 0.4;
    const vx = this.state.velocityX * (1 - smoothing) + instantVx * smoothing;
    const vy = this.state.velocityY * (1 - smoothing) + instantVy * smoothing;

    const deltaX = this.touchAxisLock === 'x' ? this.touchStartX - x : 0;
    const deltaY = this.touchAxisLock === 'y' ? this.touchStartY - y : 0;
    this.scrollTo(this.touchStartScrollX + deltaX, this.touchStartScrollY + deltaY);

    this.state.velocityX = vx;
    this.state.velocityY = vy;
    this.lastTouchX = x;
    this.lastTouchY = y;
    this.lastTouchTime = now;
  }

  /**
   * Handle touch end
   */
  handleTouchEnd(): void {
    // Start momentum if velocity is significant
    const speed =
      (this.maxScrollX > 0 ? Math.abs(this.state.velocityX) : 0) +
      (this.maxScrollY > 0 ? Math.abs(this.state.velocityY) : 0);
    if (this.options.momentum && speed > 2) {
      this.startMomentum();
    } else if (this.options.snapToMultiple) {
      this.snapToMultipleBoundary(this.options.snapToMultiple);
    }
    this.touchAxisLock = 'none';
  }

  /**
   * Start momentum animation
   */
  private startMomentum(): void {
    if (this.state.isAnimating) return;

    this.state.isAnimating = true;
    this.animateMomentum();
  }

  /**
   * Animate momentum scrolling
   */
  private animateMomentum = (): void => {
    if (!this.state.isAnimating) return;

    const { friction, minVelocity, snapToMultiple, bounceDamping } = this.options;

    // Apply velocity
    this.scrollBy(this.state.velocityX, this.state.velocityY);

    // Apply friction
    this.state.velocityX *= friction;
    this.state.velocityY *= friction;

    // Edge bounce with damping
    if (this.state.scrollY < 0) {
      this.state.scrollY *= this.options.rubberBand;
      this.state.velocityY *= -bounceDamping;
    } else if (this.state.scrollY > this.maxScrollY) {
      const over = this.state.scrollY - this.maxScrollY;
      this.state.scrollY = this.maxScrollY + over * this.options.rubberBand;
      this.state.velocityY *= -bounceDamping;
    }

    // Stop if velocity is negligible
    const speed = Math.abs(this.state.velocityX) + Math.abs(this.state.velocityY);
    if (speed < (minVelocity ?? 0.1)) {
      this.state.isAnimating = false;
      this.state.velocityX = 0;
      this.state.velocityY = 0;
      if (snapToMultiple) {
        this.snapToMultipleBoundary(snapToMultiple);
      }
      return;
    }

    // Continue animation
    this.animationFrameId = this.requestRaf(this.animateMomentum);
  };

  /**
   * Stop momentum animation
   */
  stopMomentum(): void {
    this.state.isAnimating = false;
    this.state.velocityX = 0;
    this.state.velocityY = 0;
    if (this.animationFrameId !== null) {
      this.cancelRaf(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Update snapping preference
   */
  setSnapToMultiple(multiple: number | null): void {
    this.updateOptions({ snapToMultiple: multiple });
  }

  /**
   * Snap to the nearest boundary if snapping is enabled.
   * Useful for applying snap after wheel scrolling ends (there is no "wheel end" event).
   */
  snapToMultipleIfEnabled(): void {
    const multiple = this.options.snapToMultiple;
    if (!multiple) return;
    this.snapToMultipleBoundary(multiple);
  }

  /**
   * Snap to nearest multiple boundary (vertical scroll only)
   */
  private snapToMultipleBoundary(multiple: number): void {
    if (multiple <= 0) return;
    const target = Math.round(this.state.scrollY / (this.options.itemHeight * multiple)) * (this.options.itemHeight * multiple);
    const clamped = Math.max(0, Math.min(target, this.maxScrollY));
    this.scrollTo(this.state.scrollX, clamped);
  }

  /**
   * Apply rubber banding to a scroll value
   */
  private applyRubberBand(value: number, max: number): number {
    const { rubberBand, maxOverscroll } = this.options;
    if (value < 0) {
      const overshoot = Math.max(value, -maxOverscroll);
      return overshoot * rubberBand;
    }
    if (value > max) {
      const overshoot = Math.min(value - max, maxOverscroll);
      return max + overshoot * rubberBand;
    }
    return value;
  }

  /**
   * Set scroll change callback
   */
  onScroll(callback: (range: VisibleRange) => void): void {
    this.onScrollCallback = callback;
  }

  /**
   * Notify scroll change
   */
  private notifyScrollChange(): void {
    if (this.onScrollCallback) {
      this.onScrollCallback(this.getVisibleRange());
    }
  }

  /**
   * Get current scroll state
   */
  getScrollState(): ScrollState {
    return { ...this.state };
  }

  /**
   * Get layout info
   */
  getLayout(): {
    cols: number;
    rows: number;
    totalHeight: number;
    totalWidth: number;
  } {
    return {
      cols: this.cols,
      rows: this.rows,
      totalHeight: this.totalHeight,
      totalWidth: this.totalWidth,
    };
  }

  /**
   * Get index at viewport coordinates
   */
  getIndexAtPoint(x: number, y: number): number | null {
    const { scrollX, scrollY } = this.state;
    const { itemWidth, itemHeight } = this.options;

    const effectiveScrollX = this.maxScrollX === 0 ? 0 : scrollX;
    const absoluteX = effectiveScrollX + x;
    const absoluteY = scrollY + y;

    const col = Math.floor(absoluteX / itemWidth);
    const row = Math.floor(absoluteY / itemHeight);

    if (col < 0 || col >= this.cols || row < 0 || row >= this.rows) {
      return null;
    }

    const index = row * this.cols + col;
    if (index >= this.options.totalItems) {
      return null;
    }

    return index;
  }

  /**
   * Get viewport coordinates for an index
   */
  getPointForIndex(index: number): { x: number; y: number } | null {
    if (index < 0 || index >= this.options.totalItems) {
      return null;
    }

    const col = index % this.cols;
    const row = Math.floor(index / this.cols);

    const { itemWidth, itemHeight } = this.options;
    const { scrollX, scrollY } = this.state;
    const effectiveScrollX = this.maxScrollX === 0 ? 0 : scrollX;

    return {
      x: col * itemWidth - effectiveScrollX,
      y: row * itemHeight - scrollY,
    };
  }

  /**
   * Prefetch ahead of scroll direction
   */
  prefetchAhead(direction: 'up' | 'down' | 'left' | 'right', distance: number): VisibleRange {
    const currentRange = this.getVisibleRange();

    let prefetchStartRow = currentRange.startRow;
    let prefetchEndRow = currentRange.endRow;

    if (direction === 'up') {
      prefetchStartRow = Math.max(0, prefetchStartRow - distance);
    } else if (direction === 'down') {
      prefetchEndRow = Math.min(this.rows, prefetchEndRow + distance);
    }

    // Return the prefetch range
    return {
      ...currentRange,
      startRow: prefetchStartRow,
      endRow: prefetchEndRow,
      startIndex: prefetchStartRow * this.cols,
      endIndex: Math.min(this.options.totalItems, prefetchEndRow * this.cols),
    };
  }

  /**
   * Cleanup
   */
  dispose(): void {
    this.stopMomentum();
    this.onScrollCallback = null;
  }

  private requestRaf(callback: FrameRequestCallback): number | ReturnType<typeof setTimeout> {
    if (this.useNativeRaf) {
      return globalThis.requestAnimationFrame(callback);
    }
    return globalThis.setTimeout(() => callback(performance.now()), 16);
  }

  private cancelRaf(handle: number | ReturnType<typeof setTimeout>): void {
    if (this.useNativeRaf) {
      globalThis.cancelAnimationFrame(handle as number);
      return;
    }
    globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>);
  }
}

export default VirtualScroller;
